import { SortDirection } from "@framework/types-collection";

export enum StationTrainSortField {
  custom = "custom",
  name = "name",
  alarmStatus = "alarmStatus",
  tci = "tci",
  mode = "mode",
  forecast = "forecast",
}

export interface IStationTrainSortItem {
  alarmStatus: number;
  forecastStatus: number | null;
  hasAlarmData: boolean;
  id: number;
  modeColor: string | null;
  name: string;
  tciValue: number | null;
  userSortNum: number;
}

const normalizeText = (value: string): string => value.trim().toLocaleLowerCase();

export const getAlarmStatusSortRank = (status: number, hasData = true): number => {
  if (!hasData || status === 255) return 0;
  if (status >= 90) return 4;
  if (status >= 45) return 3;
  if (status >= 30) return 2;
  return 1;
};

export const getForecastStatusSortRank = (status: number | null | undefined): number => {
  if (typeof status !== "number" || Number.isNaN(status)) {
    return -1;
  }
  return getAlarmStatusSortRank(status);
};

const compareNullableNumbers = (
  left: number | null | undefined,
  right: number | null | undefined,
  direction: SortDirection,
): number => {
  const hasLeftValue = typeof left === "number" && !Number.isNaN(left);
  const hasRightValue = typeof right === "number" && !Number.isNaN(right);

  if (!hasLeftValue && !hasRightValue) return 0;
  if (!hasLeftValue) return 1;
  if (!hasRightValue) return -1;

  const sign = direction === SortDirection.asc ? 1 : -1;
  return sign * (left - right);
};

const compareRank = (left: number, right: number, direction: SortDirection): number => {
  const sign = direction === SortDirection.asc ? 1 : -1;
  return sign * (right - left);
};

export const compareStationTrains = (
  left: IStationTrainSortItem,
  right: IStationTrainSortItem,
  field: StationTrainSortField,
  direction: SortDirection,
): number => {
  switch (field) {
    case StationTrainSortField.name: {
      const sign = direction === SortDirection.asc ? 1 : -1;
      return (
        sign *
        normalizeText(left.name).localeCompare(normalizeText(right.name), "ru", {
          sensitivity: "base",
          numeric: true,
        })
      );
    }
    case StationTrainSortField.alarmStatus:
      return (
        compareRank(
          getAlarmStatusSortRank(left.alarmStatus, left.hasAlarmData),
          getAlarmStatusSortRank(right.alarmStatus, right.hasAlarmData),
          direction,
        ) || compareNullableNumbers(left.alarmStatus, right.alarmStatus, direction)
      );
    case StationTrainSortField.tci:
      return compareNullableNumbers(left.tciValue, right.tciValue, direction);
    case StationTrainSortField.mode: {
      const leftColor = left.modeColor?.trim().toLocaleLowerCase() ?? "";
      const rightColor = right.modeColor?.trim().toLocaleLowerCase() ?? "";
      const sign = direction === SortDirection.asc ? 1 : -1;

      if (!leftColor && !rightColor) return 0;
      if (!leftColor) return 1;
      if (!rightColor) return -1;

      return sign * leftColor.localeCompare(rightColor, undefined, { sensitivity: "base" });
    }
    case StationTrainSortField.forecast: {
      const leftRank = getForecastStatusSortRank(left.forecastStatus);
      const rightRank = getForecastStatusSortRank(right.forecastStatus);

      if (leftRank < 0 && rightRank < 0) return 0;
      if (leftRank < 0) return 1;
      if (rightRank < 0) return -1;

      return compareRank(leftRank, rightRank, direction);
    }
    case StationTrainSortField.custom:
    default: {
      const sign = direction === SortDirection.asc ? 1 : -1;
      return sign * (left.userSortNum - right.userSortNum) || left.id - right.id;
    }
  }
};

export const sortStationTrainIds = (
  ids: number[],
  itemsById: Record<number, IStationTrainSortItem>,
  field: StationTrainSortField,
  direction: SortDirection,
): number[] =>
  [...ids].sort((leftId, rightId) => {
    const left = itemsById[leftId];
    const right = itemsById[rightId];

    if (!left || !right) {
      return 0;
    }

    return compareStationTrains(left, right, field, direction) || left.id - right.id;
  });
